// pages/Contabilidad.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import ModalPinEmpresa from '../components/ModalPinEmpresa';
import { obtenerFechaLocal } from '../utils/dateUtils';

const fmtM = (n) => new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtF = (d) => { if (!d) return '—'; const [y, m, day] = String(d).substring(0, 10).split('-'); return `${day}/${m}/${y}`; };

function SelectorEmpresa({ empresas, empresaId, onChange }) {
  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', minWidth: 260 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginBottom: 6 }}>EMPRESA ACTIVA</div>
      <select
        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #93c5fd', fontSize: 13, fontWeight: 600 }}
        value={empresaId || ''}
        onChange={e => onChange(parseInt(e.target.value))}
      >
        {empresas.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
      </select>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
        <Link to="/empresas-contables" style={{ color: '#2563eb' }}>Gestionar empresas</Link>
      </div>
    </div>
  );
}

const hoy    = new Date();
const primerDia = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}-01`;
const hoyStr = obtenerFechaLocal();

export default function Contabilidad() {
  const navigate = useNavigate();
  const [tab, setTab]         = useState('plan');
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(null);
  const [modalPin, setModalPin] = useState(null); // empresa pendiente de PIN

  useEffect(() => {
    api.get('/empresas-contables').then(r => {
      const lista = r.data.empresas || r.data || [];
      setEmpresas(lista);
      const guardado = parseInt(localStorage.getItem('empresa_contable_id'));
      const existe   = lista.find(e => e.id === guardado);
      const inicial  = existe ? guardado : (lista[0]?.id || null);
      if (inicial) {
        const emp = lista.find(e => e.id === inicial);
        localStorage.setItem('empresa_contable_id',    inicial);
        localStorage.setItem('empresa_contable_nombre', emp?.nombre || '');
        setEmpresaId(inicial);
      }
    }).catch(() => {});
  }, []);

  const cambiarEmpresa = (id) => {
    const emp = empresas.find(e => e.id === id);
    if (!emp) return;
    if (emp.tiene_pin) {
      setModalPin(emp); // solicitar PIN antes de cambiar
      return;
    }
    // Sin PIN → cambiar directo
    localStorage.setItem('empresa_contable_id',    emp.id);
    localStorage.setItem('empresa_contable_nombre', emp.nombre);
    setEmpresaId(emp.id);
    window.location.reload();
  };

  const onPinExito = (emp) => {
    setModalPin(null);
    localStorage.setItem('empresa_contable_id',    emp.id);
    localStorage.setItem('empresa_contable_nombre', emp.nombre);
    setEmpresaId(emp.id);
    window.location.reload();
  };

  const tabs = [
    { key: 'plan',     label: '📒 Plan de Cuentas' },
    { key: 'asientos', label: '✏ Asientos' },
    { key: 'balanza',  label: '⚖ Balanza' },
    { key: 'mayor',    label: '📖 Mayor General' },
    { key: 'balance',  label: '📊 Balance General' },
    { key: 'resultados', label: '📈 Estado de Resultados' },
    { key: 'cierres',    label: '🔒 Cierres' },
  ];

  return (
    <div style={sx.page}>
      <div style={sx.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <button onClick={() => navigate('/facturas')} style={sx.btnVolver}>← Módulo Contable</button>
            <h1 style={sx.titulo}>Contabilidad</h1>
            <p style={sx.subtitulo}>Plan de cuentas · Asientos · Mayor · Balance · Estado de Resultados</p>
          </div>
          {empresas.length > 0 && (
            <SelectorEmpresa empresas={empresas} empresaId={empresaId} onChange={cambiarEmpresa} />
          )}
        </div>
      </div>

      {!empresaId ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando empresa contable…</div>
      ) : (
        <>
          <div style={sx.tabsBar}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ ...sx.tabBtn, ...(tab === t.key ? sx.tabActivo : {}) }}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'plan'       && <TabPlanCuentas />}
          {tab === 'asientos'   && <TabAsientos />}
          {tab === 'balanza'    && <TabBalanza />}
          {tab === 'mayor'      && <TabMayor />}
          {tab === 'balance'    && <TabBalance />}
          {tab === 'resultados' && <TabResultados />}
          {tab === 'cierres'    && <TabCierres />}
        </>
      )}

      {/* Modal PIN para cambio de empresa */}
      {modalPin && (
        <ModalPinEmpresa
          empresa={modalPin}
          onExito={onPinExito}
          onCancelar={() => setModalPin(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
//  Plan de Cuentas
// ═══════════════════════════════════════════════════
function TabPlanCuentas() {
  const [cuentas,     setCuentas]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null);   // { modo:'crear'|'editar', padre?, cuenta? }
  const [colapsadas,  setColapsadas]  = useState(() => new Set());
  const [form, setForm] = useState({ codigo: '', nombre: '', tipo: 'activo', naturaleza: 'deudora', descripcion: '' });

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contabilidad/cuentas');
      setCuentas(data.cuentas || []);
    } catch (e) { Swal.fire('Error', 'No se pudo cargar el plan de cuentas', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const cargarEstandar = async () => {
    const conf = await Swal.fire({
      title: '¿Cargar Plan Estándar?',
      text: 'Se cargará el plan de cuentas estándar para República Dominicana (40+ cuentas)',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cargar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0066cc'
    });
    if (!conf.isConfirmed) return;
    try {
      const { data } = await api.post('/contabilidad/cuentas/plan-estandar');
      Swal.fire({ icon: 'success', title: '✅ Plan cargado', text: data.mensaje, timer: 3000 });
      cargar();
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al cargar', 'error');
    }
  };

  // ── Colapsar / expandir ───────────────────────────────────────────────────
  const toggleColapso = (id) => setColapsadas(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const colapsarTodo  = () => setColapsadas(new Set(cuentas.map(c => c.id)));
  const expandirTodo  = () => setColapsadas(new Set());

  // ── Aperturas del modal ───────────────────────────────────────────────────
  const abrirCrearRaiz = () => {
    setForm({ codigo: '', nombre: '', tipo: 'activo', naturaleza: 'deudora', descripcion: '' });
    setModal({ modo: 'crear', padre: null });
  };

  // "+ subcuenta": hereda padre, tipo y naturaleza, y presugiere el código
  const abrirCrearHijo = (padre) => {
    setForm({
      codigo:      siguienteCodigoHijo(padre),
      nombre:      '',
      tipo:        padre.tipo,
      naturaleza:  padre.naturaleza || 'deudora',
      descripcion: ''
    });
    setModal({ modo: 'crear', padre });
  };

  const abrirEditar = (cuenta) => {
    setForm({
      codigo:      cuenta.codigo,
      nombre:      cuenta.nombre,
      tipo:        cuenta.tipo,
      naturaleza:  cuenta.naturaleza || 'deudora',
      descripcion: cuenta.descripcion || '',
      acepta_movimientos: cuenta.acepta_movimientos,
      activa:      cuenta.activa !== false
    });
    setModal({ modo: 'editar', cuenta });
  };

  // ── Guardar ───────────────────────────────────────────────────────────────
  const guardar = async () => {
    if (modal.modo === 'crear') {
      if (!form.codigo || !form.nombre || !form.tipo) {
        return Swal.fire('Atención', 'Código, nombre y tipo son requeridos', 'warning');
      }
      try {
        await api.post('/contabilidad/cuentas', {
          codigo: form.codigo, nombre: form.nombre, tipo: form.tipo,
          naturaleza: form.naturaleza, descripcion: form.descripcion,
          padre_id: modal.padre ? modal.padre.id : null
        });
        setModal(null);
        cargar();
        Swal.fire({ icon: 'success', title: 'Cuenta creada', timer: 1800, showConfirmButton: false });
      } catch (e) { mostrarError(e, 'Error al crear la cuenta'); }
      return;
    }

    // Editar: sólo se envían los campos que el backend permite para este nivel
    const c = modal.cuenta;
    const cambios = esEstructural(c)
      ? { descripcion: form.descripcion }
      : {
          nombre: form.nombre,
          descripcion: form.descripcion,
          naturaleza: form.naturaleza,
          acepta_movimientos: form.acepta_movimientos,
          activa: form.activa
        };
    try {
      await api.put(`/contabilidad/cuentas/${c.id}`, cambios);
      setModal(null);
      cargar();
      Swal.fire({ icon: 'success', title: 'Cuenta actualizada', timer: 1800, showConfirmButton: false });
    } catch (e) { mostrarError(e, 'Error al actualizar la cuenta'); }
  };

  const eliminar = async (c) => {
    const conf = await Swal.fire({
      title: '¿Eliminar cuenta?',
      html: `<b>${c.codigo}</b> — ${c.nombre}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626'
    });
    if (!conf.isConfirmed) return;
    try {
      await api.delete(`/contabilidad/cuentas/${c.id}`);
      cargar();
      Swal.fire({ icon: 'success', title: 'Cuenta eliminada', timer: 1800, showConfirmButton: false });
    } catch (e) { mostrarError(e, 'No se pudo eliminar'); }
  };

  const tipoColor = (tipo) => {
    const m = { activo: '#059669', pasivo: '#dc2626', capital: '#7c3aed', ingreso: '#0066cc', costo: '#f97316', egreso: '#6c757d' };
    return m[tipo] || '#6c757d';
  };

  // Filas visibles: se ocultan los descendientes de cualquier nodo colapsado
  const cuentasFlat = aplanarCuentas(cuentas);
  const visibles    = filtrarVisibles(cuentas, colapsadas);
  const editando    = modal?.modo === 'editar' ? modal.cuenta : null;
  const bloqueado   = editando ? esEstructural(editando) : false;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <p style={{ color: '#6c757d', margin: 0 }}>{cuentasFlat.length} cuentas registradas</p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {cuentasFlat.length > 0 && (
            <>
              <button onClick={expandirTodo} style={sx.btnSecundario} title="Expandir todo">⊞ Expandir</button>
              <button onClick={colapsarTodo} style={sx.btnSecundario} title="Colapsar todo">⊟ Colapsar</button>
            </>
          )}
          {cuentasFlat.length === 0 && (
            <button onClick={cargarEstandar} style={{ ...sx.btnPrimario, background: '#059669' }}>
              ⬇ Cargar Plan Estándar RD
            </button>
          )}
          <button onClick={abrirCrearRaiz} style={sx.btnPrimario}>+ Nueva Cuenta</button>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={sx.tablaWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Código','Nombre','Tipo','Naturaleza','Nivel','Estado','Acciones'].map(h => <th key={h} style={sx.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {visibles.map((c, i) => {
                const tieneHijos = (c.hijos?.length || 0) > 0;
                const cerrado    = colapsadas.has(c.id);
                const esPadre    = c.acepta_movimientos === false;
                return (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={{ ...sx.td, fontFamily: 'monospace', fontWeight: esPadre ? 700 : 400 }}>{c.codigo}</td>
                    <td style={{ ...sx.td, paddingLeft: `${(c._nivel) * 1.5 + 1}rem`, fontWeight: esPadre ? 700 : 400 }}>
                      {tieneHijos ? (
                        <button
                          onClick={() => toggleColapso(c.id)}
                          title={cerrado ? 'Expandir' : 'Colapsar'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 6, color: '#6c757d', fontSize: '0.8rem', width: 14 }}
                        >{cerrado ? '▶' : '▼'}</button>
                      ) : <span style={{ display: 'inline-block', width: 20 }} />}
                      {c.nombre}
                      {tieneHijos && cerrado && (
                        <span style={{ color: '#94a3b8', fontSize: '0.78rem', marginLeft: 6 }}>({contarDescendientes(c)})</span>
                      )}
                    </td>
                    <td style={sx.td}><span style={{ ...sx.badge, background: `${tipoColor(c.tipo)}20`, color: tipoColor(c.tipo) }}>{c.tipo}</span></td>
                    <td style={sx.td}>{c.naturaleza}</td>
                    <td style={sx.td}>{c._nivel + 1}</td>
                    <td style={sx.td}><span style={{ ...sx.badge, background: c.activa !== false ? '#d1fae5' : '#fee2e2', color: c.activa !== false ? '#065f46' : '#991b1b' }}>{c.activa !== false ? 'Activa' : 'Inactiva'}</span></td>
                    <td style={{ ...sx.td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => abrirCrearHijo(c)} style={sx.btnIcono} title={`Agregar subcuenta bajo ${c.codigo}`}>＋</button>
                      <button onClick={() => abrirEditar(c)}    style={sx.btnIcono} title="Editar">✏️</button>
                      <button onClick={() => eliminar(c)}       style={{ ...sx.btnIcono, color: '#dc2626' }} title="Eliminar">🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          titulo={
            modal.modo === 'editar'
              ? `Editar cuenta ${modal.cuenta.codigo}`
              : modal.padre
                ? `Nueva subcuenta de ${modal.padre.codigo} — ${modal.padre.nombre}`
                : 'Nueva Cuenta'
          }
          onClose={() => setModal(null)}
          onGuardar={guardar}
        >
          {modal.modo === 'crear' && modal.padre && (
            <div style={sx.avisoInfo}>
              Se creará como <b>nivel {(modal.padre.nivel || 1) + 1}</b>, colgando de <b>{modal.padre.codigo}</b>.
              El nivel lo calcula el servidor a partir del padre.
              {modal.padre.acepta_movimientos && (
                <div style={{ marginTop: 6 }}>
                  ⚠️ <b>{modal.padre.codigo}</b> pasará a ser cuenta de agrupación y dejará de aceptar movimientos directos.
                </div>
              )}
            </div>
          )}

          {bloqueado && (
            <div style={sx.avisoBloqueo}>
              🔒 Cuenta de sistema — estructura no editable.
              Sólo se puede modificar la descripción en cuentas de nivel 1 y 2.
            </div>
          )}

          <div style={sx.fila2col}>
            <Campo label="Código *">
              <input
                value={form.codigo}
                onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))}
                style={{ ...sx.input, ...(modal.modo === 'editar' ? sx.inputRO : {}) }}
                placeholder="Ej: 1.1.7"
                disabled={modal.modo === 'editar'}
              />
            </Campo>
            <Campo label="Tipo *">
              <select
                value={form.tipo}
                onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                style={{ ...sx.input, ...(modal.modo === 'editar' ? sx.inputRO : {}) }}
                disabled={modal.modo === 'editar'}
              >
                {['activo','pasivo','capital','ingreso','costo','egreso'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Campo>
          </div>

          <Campo label="Nombre *">
            <input
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              style={{ ...sx.input, ...(bloqueado ? sx.inputRO : {}) }}
              placeholder="Nombre de la cuenta"
              disabled={bloqueado}
            />
          </Campo>

          <div style={sx.fila2col}>
            <Campo label="Naturaleza">
              <select
                value={form.naturaleza}
                onChange={e => setForm(f => ({ ...f, naturaleza: e.target.value }))}
                style={{ ...sx.input, ...(bloqueado ? sx.inputRO : {}) }}
                disabled={bloqueado}
              >
                <option value="deudora">Deudora</option>
                <option value="acreedora">Acreedora</option>
              </select>
            </Campo>
            {modal.modo === 'editar' && !bloqueado && (
              <Campo label="Estado">
                <select
                  value={form.activa ? '1' : '0'}
                  onChange={e => setForm(f => ({ ...f, activa: e.target.value === '1' }))}
                  style={sx.input}
                >
                  <option value="1">Activa</option>
                  <option value="0">Inactiva</option>
                </select>
              </Campo>
            )}
          </div>

          {modal.modo === 'editar' && !bloqueado && (
            <Campo label="Acepta movimientos">
              <select
                value={form.acepta_movimientos ? '1' : '0'}
                onChange={e => setForm(f => ({ ...f, acepta_movimientos: e.target.value === '1' }))}
                style={sx.input}
                disabled={(modal.cuenta.hijos?.length || 0) > 0}
              >
                <option value="1">Sí — se puede postear directamente</option>
                <option value="0">No — cuenta de agrupación</option>
              </select>
              {(modal.cuenta.hijos?.length || 0) > 0 && (
                <small style={{ color: '#6c757d' }}>
                  Tiene {modal.cuenta.hijos.length} subcuenta(s): es cuenta de agrupación obligatoriamente.
                </small>
              )}
            </Campo>
          )}

          <Campo label="Descripción">
            <textarea
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              style={{ ...sx.input, minHeight: 70, resize: 'vertical' }}
              placeholder="Notas internas sobre el uso de esta cuenta"
            />
          </Campo>
        </Modal>
      )}
    </div>
  );
}

// ── Helpers del plan de cuentas ─────────────────────────────────────────────

// Nivel 1 y 2 son estructura del plan: sólo se les puede editar la descripción.
// Debe coincidir con CAMPOS_EDITABLES_RAIZ en contabilidadController.js.
function esEstructural(cuenta) {
  return (cuenta?.nivel || 1) <= 2;
}

// Presugiere el código del próximo hijo: '1.1' -> '1.1.3' si ya existen .1 y .2
function siguienteCodigoHijo(padre) {
  const hijos = padre.hijos || [];
  let max = 0;
  for (const h of hijos) {
    const resto = String(h.codigo).startsWith(`${padre.codigo}.`)
      ? String(h.codigo).slice(padre.codigo.length + 1)
      : '';
    const n = parseInt(resto.split('.')[0], 10);
    if (Number.isInteger(n) && n > max) max = n;
  }
  return `${padre.codigo}.${max + 1}`;
}

function contarDescendientes(cuenta) {
  return (cuenta.hijos || []).reduce((s, h) => s + 1 + contarDescendientes(h), 0);
}

// Aplana el árbol saltando los descendientes de los nodos colapsados.
function filtrarVisibles(cuentas, colapsadas, nivel = 0) {
  const out = [];
  for (const c of cuentas) {
    out.push({ ...c, _nivel: nivel });
    if (!colapsadas.has(c.id) && c.hijos?.length) {
      out.push(...filtrarVisibles(c.hijos, colapsadas, nivel + 1));
    }
  }
  return out;
}

// Los errores del backend traen mensaje y codigo_error: se muestran tal cual,
// porque explican qué hay que hacer (migrar movimientos, borrar hijos, etc.).
function mostrarError(e, fallback) {
  const d = e.response?.data;
  Swal.fire({
    icon: 'error',
    title: d?.codigo_error ? 'Operación no permitida' : 'Error',
    text: d?.mensaje || fallback,
  });
}

// ═══════════════════════════════════════════════════
//  Asientos Contables
// ═══════════════════════════════════════════════════
function TabAsientos() {
  const [asientos, setAsientos] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [cuentas,  setCuentas]  = useState([]);
  const [filtro, setFiltro] = useState({ fecha_desde: primerDia, fecha_hasta: hoyStr, estado: '' });
  const [modalRetro, setModalRetro] = useState(false);
  const [retro, setRetro] = useState({ fecha_desde: primerDia, fecha_hasta: hoyStr });
  const [retroLoading, setRetroLoading] = useState(false);

  const LINEA_VACIA = { cuenta_id: '', descripcion: '', debito: 0, credito: 0 };
  const [form, setForm] = useState({
    fecha: hoyStr, descripcion: '', tipo: 'manual',
    lineas: [{ ...LINEA_VACIA }, { ...LINEA_VACIA }]
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filtro);
      const [rA, rC] = await Promise.all([
        api.get(`/contabilidad/asientos?${params}`),
        api.get('/contabilidad/cuentas')
      ]);
      setAsientos(rA.data.asientos || []);
      setCuentas(aplanarCuentas(rC.data.cuentas || []).filter(c => c.acepta_movimientos));
    } catch (e) { Swal.fire('Error', 'No se pudieron cargar los asientos', 'error'); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const setLinea = (i, k, v) => setForm(f => {
    const lineas = [...f.lineas];
    lineas[i] = { ...lineas[i], [k]: v };
    return { ...f, lineas };
  });
  const addLinea    = () => setForm(f => ({ ...f, lineas: [...f.lineas, { ...LINEA_VACIA }] }));
  const removeLinea = (i) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, idx) => idx !== i) }));

  const totalD = form.lineas.reduce((s, l) => s + (parseFloat(l.debito)  || 0), 0);
  const totalC = form.lineas.reduce((s, l) => s + (parseFloat(l.credito) || 0), 0);
  const cuadrado = Math.abs(totalD - totalC) < 0.01;

  const guardar = async () => {
    if (!form.descripcion) return Swal.fire('Atención', 'La descripción es requerida', 'warning');

    const lineasConCuenta = form.lineas.filter(l => l.cuenta_id);
    const lineasSinCuenta = form.lineas.filter(l => !l.cuenta_id && (parseFloat(l.debito) > 0 || parseFloat(l.credito) > 0));
    if (lineasConCuenta.length < 2) {
      return Swal.fire({
        icon: 'warning',
        title: 'Asiento incompleto',
        html: `Debes completar al menos <b>2 líneas</b> con su cuenta asignada.<br><br>
               Cada asiento necesita mínimo una línea de <b>débito</b> y una de <b>crédito</b>.<br>
               Ejemplo: Efectivo (débito RD$10,000) / Ingresos por Ventas (crédito RD$10,000).`,
      });
    }
    if (lineasSinCuenta.length > 0) {
      const { isConfirmed } = await Swal.fire({
        icon: 'warning',
        title: 'Hay líneas sin cuenta',
        html: `${lineasSinCuenta.length} línea(s) tienen monto pero no tienen cuenta asignada y serán ignoradas.<br><br>¿Deseas continuar de todas formas?`,
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Corregir',
        confirmButtonColor: '#f59e0b',
      });
      if (!isConfirmed) return;
    }
    if (!cuadrado) return Swal.fire({
      icon: 'error',
      title: 'El asiento no cuadra',
      html: `Total Débitos: <b>RD$${fmtM(totalD)}</b><br>Total Créditos: <b>RD$${fmtM(totalC)}</b><br><br>
             La diferencia de <b>RD$${fmtM(Math.abs(totalD - totalC))}</b> debe ser cero.<br>
             Revisa los montos de cada línea.`,
    });
    try {
      await api.post('/contabilidad/asientos', form);
      setModal(false);
      setForm({ fecha: hoyStr, descripcion: '', tipo: 'manual', lineas: [{ ...LINEA_VACIA }, { ...LINEA_VACIA }] });
      cargar();
      Swal.fire({ icon: 'success', title: 'Asiento creado', timer: 1800, showConfirmButton: false });
    } catch (e) {
      const msg = e.response?.data?.mensaje || 'Error al guardar';
      const esCerrado = msg.toLowerCase().includes('cerrado');
      Swal.fire({
        icon: esCerrado ? 'warning' : 'error',
        title: esCerrado ? '⚠ Período Cerrado' : 'Error',
        text: esCerrado
          ? `No puedes registrar asientos en un período que ya fue cerrado. Cambia la fecha a un período abierto.`
          : msg,
      });
    }
  };

  const publicar = async (id) => {
    const c = await Swal.fire({ title: '¿Publicar asiento?', text: 'Una vez publicado no se puede editar', icon: 'question', showCancelButton: true, confirmButtonText: 'Publicar', cancelButtonText: 'Cancelar', confirmButtonColor: '#0066cc' });
    if (!c.isConfirmed) return;
    try { await api.post(`/contabilidad/asientos/${id}/publicar`); cargar(); } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
  };

  const anular = async (id, numero) => {
    const c = await Swal.fire({ title: `¿Anular asiento ${numero}?`, text: 'Esta acción no se puede revertir. El asiento quedará marcado como anulado.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí, anular', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc2626' });
    if (!c.isConfirmed) return;
    try { await api.post(`/contabilidad/asientos/${id}/anular`); cargar(); Swal.fire({ icon: 'success', title: 'Asiento anulado', timer: 1500, showConfirmButton: false }); }
    catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error al anular', 'error'); }
  };

  const ejecutarRetroactivo = async () => {
    setRetroLoading(true);
    try {
      const res = await api.post('/contabilidad/retroalimentar');
      const { resultados } = res.data;
      setModalRetro(false);
      cargar();
      Swal.fire({
        icon: 'success',
        title: 'Proceso completado',
        html: `<b>${resultados.facturas.procesadas}</b> asientos de facturas creados<br><b>${resultados.pagos.procesados}</b> asientos de cobros creados`,
        confirmButtonColor: '#0066cc'
      });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al ejecutar retroalimentación', 'error');
    } finally { setRetroLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={sx.inputSm} />
          <span style={{ color: '#6c757d' }}>—</span>
          <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={sx.inputSm} />
          <select value={filtro.estado} onChange={e => setFiltro(f => ({ ...f, estado: e.target.value }))} style={sx.inputSm}>
            <option value="">Todos</option><option value="borrador">Borrador</option><option value="publicado">Publicado</option><option value="anulado">Anulado</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setModalRetro(true)} style={{ ...sx.btnSecundario, background: '#6f42c1', color: '#fff', borderColor: '#6f42c1' }}>⟳ Retroactivo</button>
          <button onClick={() => setModal(true)} style={sx.btnPrimario}>+ Nuevo Asiento</button>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={sx.tablaWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead><tr>{['Número','Fecha','Descripción','Débitos','Créditos','Estado','Acción'].map(h => <th key={h} style={sx.th}>{h}</th>)}</tr></thead>
            <tbody>
              {asientos.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>No hay asientos en el período</td></tr>
              ) : asientos.map((a, i) => (
                <tr key={a.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{ ...sx.td, fontFamily: 'monospace', fontWeight: 600 }}>{a.numero}</td>
                  <td style={sx.td}>{fmtF(a.fecha)}</td>
                  <td style={{ ...sx.td, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.descripcion}</td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>{fmtM(a.total_debito)}</td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>{fmtM(a.total_credito)}</td>
                  <td style={sx.td}><BadgeEstadoAsiento estado={a.estado} /></td>
                  <td style={{ ...sx.td, display: 'flex', gap: 6 }}>
                    {a.estado === 'borrador' && (
                      <button onClick={() => publicar(a.id)} style={{ background: '#0066cc', color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                        Publicar
                      </button>
                    )}
                    {a.estado === 'publicado' && (
                      <button onClick={() => anular(a.id, a.numero)} style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                        Anular
                      </button>
                    )}
                    {a.estado === 'anulado' && (
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal titulo="Nuevo Asiento Contable" onClose={() => setModal(false)} onGuardar={guardar} ancho={850}>
          <div style={sx.fila2col}>
            <Campo label="Fecha *"><input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={sx.input} /></Campo>
            <Campo label="Tipo">
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={sx.input}>
                <option value="manual">Manual</option><option value="ajuste">Ajuste</option><option value="cierre">Cierre</option>
              </select>
            </Campo>
          </div>
          <Campo label="Descripción *"><input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={sx.input} placeholder="Descripción del asiento..." /></Campo>

          {/* Líneas */}
          <div style={{ margin: '1rem 0 0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <label style={{ ...sx.label, margin: 0 }}>Líneas del asiento</label>
              <button onClick={addLinea} style={sx.btnSecundario}>+ Línea</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead><tr><th style={sx.th}>Cuenta</th><th style={sx.th}>Descripción</th><th style={{ ...sx.th, textAlign: 'right' }}>Débito</th><th style={{ ...sx.th, textAlign: 'right' }}>Crédito</th><th style={sx.th}></th></tr></thead>
                <tbody>
                  {form.lineas.map((l, i) => (
                    <tr key={i}>
                      <td style={{ padding: '0.3rem' }}>
                        <select value={l.cuenta_id} onChange={e => setLinea(i, 'cuenta_id', e.target.value)} style={{ ...sx.inputSm, width: '100%' }}>
                          <option value="">Seleccionar cuenta</option>
                          {cuentas.map(c => <option key={c.id} value={c.id}>{c.codigo} – {c.nombre}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.3rem' }}><input value={l.descripcion} onChange={e => setLinea(i, 'descripcion', e.target.value)} style={{ ...sx.inputSm, width: '100%' }} placeholder="Opcional" /></td>
                      <td style={{ padding: '0.3rem' }}><input type="number" step="any" min="0" value={l.debito} onChange={e => setLinea(i, 'debito', e.target.value)} style={{ ...sx.inputSm, width: '100%', textAlign: 'right' }} /></td>
                      <td style={{ padding: '0.3rem' }}><input type="number" step="any" min="0" value={l.credito} onChange={e => setLinea(i, 'credito', e.target.value)} style={{ ...sx.inputSm, width: '100%', textAlign: 'right' }} /></td>
                      <td style={{ padding: '0.3rem' }}>
                        {form.lineas.length > 2 && <button onClick={() => removeLinea(i)} style={{ background: '#fee2e2', border: 'none', borderRadius: 5, color: '#dc3545', padding: '0.3rem 0.5rem', cursor: 'pointer', fontWeight: 700 }}>✕</button>}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: cuadrado ? '#f0fdf4' : '#fef2f2', fontWeight: 700 }}>
                    <td colSpan={2} style={{ padding: '0.6rem 1rem', color: cuadrado ? '#065f46' : '#991b1b' }}>
                      {cuadrado ? '✅ Asiento cuadrado' : '❌ No cuadra'}
                    </td>
                    <td style={{ padding: '0.6rem 0.3rem', textAlign: 'right', color: '#1a1a2e' }}>{fmtM(totalD)}</td>
                    <td style={{ padding: '0.6rem 0.3rem', textAlign: 'right', color: '#1a1a2e' }}>{fmtM(totalC)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      {modalRetro && (
        <Modal titulo="Retroalimentar Asientos Históricos" onClose={() => setModalRetro(false)} onGuardar={ejecutarRetroactivo} ancho={480} labelGuardar="▶ Ejecutar">
          <p style={{ color: '#555', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Genera asientos contables automáticos para todas las facturas y cobros que aún no tienen asiento registrado.
          </p>
          <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#856404' }}>
            <b>Nota:</b> Solo se crean asientos para transacciones sin asiento previo. Las ya contabilizadas se omiten automáticamente.
          </div>
          {retroLoading && <p style={{ textAlign: 'center', marginTop: '1rem', color: '#0066cc' }}>Procesando asientos históricos...</p>}
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
//  Mayor General
// ═══════════════════════════════════════════════════
function TabMayor() {
  const [mayor,  setMayor]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro,  setFiltro]  = useState({ fecha_desde: primerDia, fecha_hasta: hoyStr });

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filtro);
      const { data } = await api.get(`/contabilidad/mayor?${params}`);
      setMayor(data.mayor || []);
    } catch (e) { Swal.fire('Error', 'No se pudo cargar el mayor', 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={sx.inputSm} />
        <span style={{ color: '#6c757d' }}>—</span>
        <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={sx.inputSm} />
        <button onClick={cargar} style={sx.btnPrimario} disabled={loading}>{loading ? 'Cargando...' : '📖 Generar Mayor'}</button>
      </div>

      {loading ? <Spinner /> : mayor.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📖</div>
          Haz clic en "Generar Mayor" para ver los movimientos por cuenta
        </div>
      ) : (
        <div>
          {mayor.map(c => (
            <div key={c.cuenta_id} style={{ ...sx.card, marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{c.cuenta?.codigo} – {c.cuenta?.nombre}</h3>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>
                  <span style={{ color: '#0066cc' }}>D: {fmtM(c.total_debito)}</span>
                  <span style={{ color: '#dc2626' }}>C: {fmtM(c.total_credito)}</span>
                  <span style={{ color: c.saldo >= 0 ? '#065f46' : '#991b1b', fontWeight: 700 }}>Saldo: {fmtM(Math.abs(c.saldo))}</span>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead><tr>{['Fecha','Asiento','Descripción','Débito','Crédito'].map(h => <th key={h} style={{ ...sx.th, textAlign: ['Débito','Crédito'].includes(h) ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {c.movimientos.map((m, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      <td style={sx.td}>{fmtF(m.fecha)}</td>
                      <td style={{ ...sx.td, fontFamily: 'monospace' }}>{m.asiento}</td>
                      <td style={sx.td}>{m.descripcion}</td>
                      <td style={{ ...sx.td, textAlign: 'right', color: m.debito > 0 ? '#0066cc' : '#6c757d' }}>{m.debito > 0 ? fmtM(m.debito) : '—'}</td>
                      <td style={{ ...sx.td, textAlign: 'right', color: m.credito > 0 ? '#dc2626' : '#6c757d' }}>{m.credito > 0 ? fmtM(m.credito) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
//  Balance General
// ═══════════════════════════════════════════════════
function TabBalance() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fecha,   setFecha]   = useState(hoyStr);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contabilidad/balance?fecha_hasta=${fecha}`);
      setBalance(data.balance);
    } catch (e) { Swal.fire('Error', 'No se pudo cargar el balance', 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.5rem' }}>
        <label style={{ fontWeight: 600, color: '#495057' }}>Fecha de corte:</label>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={sx.inputSm} />
        <button onClick={cargar} style={sx.btnPrimario} disabled={loading}>{loading ? 'Cargando...' : '⚖ Generar Balance'}</button>
      </div>

      {loading ? <Spinner /> : !balance ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚖</div>
          Selecciona la fecha de corte y haz clic en "Generar Balance"
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Activos */}
          <div style={sx.card}>
            <h3 style={{ ...sx.cardTitulo, color: '#059669' }}>ACTIVOS</h3>
            <SeccionBalance items={balance.activos} total={balance.total_activos} colorTotal="#059669" />
          </div>
          {/* Pasivos + Capital */}
          <div>
            <div style={{ ...sx.card, marginBottom: '1rem' }}>
              <h3 style={{ ...sx.cardTitulo, color: '#dc2626' }}>PASIVOS</h3>
              <SeccionBalance items={balance.pasivos} total={balance.total_pasivos} colorTotal="#dc2626" />
            </div>
            <div style={sx.card}>
              <h3 style={{ ...sx.cardTitulo, color: '#7c3aed' }}>CAPITAL</h3>
              <SeccionBalance items={balance.capital} total={balance.total_capital} colorTotal="#7c3aed" />
              {/* Resultado del Ejercicio (Ingresos - Costos - Gastos del período) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #f1f3f5' }}>
                <span style={{ color: '#495057', fontSize: '0.875rem', fontStyle: 'italic' }}>Resultado del Ejercicio</span>
                <span style={{ fontWeight: 600, fontSize: '0.875rem', color: balance.resultado_ejercicio >= 0 ? '#059669' : '#dc2626' }}>
                  {balance.resultado_ejercicio >= 0 ? '' : '-'}RD$ {fmtM(Math.abs(balance.resultado_ejercicio))}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', marginTop: '0.5rem', borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
                <span style={{ color: '#7c3aed' }}>Total Capital</span>
                <span style={{ color: '#7c3aed', fontSize: '1rem' }}>RD$ {fmtM(balance.total_capital + balance.resultado_ejercicio)}</span>
              </div>
            </div>
            <div style={{ background: '#1e3a8a', borderRadius: 8, padding: '1rem 1.5rem', marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#fff', fontWeight: 700 }}>Total Pasivos + Capital</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>RD$ {fmtM(balance.total_pasivos_capital)}</div>
                {Math.abs(balance.total_activos - balance.total_pasivos_capital) < 1 ? (
                  <div style={{ color: '#86efac', fontSize: '0.75rem' }}>✓ Balance cuadrado</div>
                ) : (
                  <div style={{ color: '#fca5a5', fontSize: '0.75rem' }}>⚠ Diferencia: RD$ {fmtM(Math.abs(balance.total_activos - balance.total_pasivos_capital))}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SeccionBalance({ items, total, colorTotal }) {
  return (
    <>
      {items.map(i => (
        <div key={i.cuenta_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #f1f3f5' }}>
          <span style={{ color: '#495057', fontSize: '0.875rem' }}>{i.codigo} – {i.nombre}</span>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{fmtM(i.saldo)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', marginTop: '0.5rem', borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
        <span style={{ color: colorTotal }}>Total</span>
        <span style={{ color: colorTotal, fontSize: '1rem' }}>RD$ {fmtM(total)}</span>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════
//  Estado de Resultados
// ═══════════════════════════════════════════════════
function TabResultados() {
  const [estado,  setEstado]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtro,  setFiltro]  = useState({ fecha_desde: primerDia, fecha_hasta: hoyStr });

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filtro);
      const { data } = await api.get(`/contabilidad/estado-resultados?${params}`);
      setEstado(data.estado);
    } catch (e) { Swal.fire('Error', 'No se pudo cargar el estado de resultados', 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={sx.inputSm} />
        <span style={{ color: '#6c757d' }}>—</span>
        <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={sx.inputSm} />
        <button onClick={cargar} style={sx.btnPrimario} disabled={loading}>{loading ? 'Cargando...' : '📈 Generar Estado'}</button>
      </div>

      {loading ? <Spinner /> : !estado ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📈</div>
          Selecciona el período y genera el estado de resultados
        </div>
      ) : (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ ...sx.card, textAlign: 'center', background: '#1e3a8a', marginBottom: '1.25rem' }}>
            <h2 style={{ color: '#fff', margin: 0, fontSize: '1.2rem' }}>Estado de Resultados</h2>
            <p style={{ color: '#93c5fd', margin: '0.25rem 0 0' }}>{fmtF(estado.periodo.inicio)} — {fmtF(estado.periodo.fin)}</p>
          </div>

          <GrupoER titulo="INGRESOS" items={estado.ingresos} total={estado.total_ingresos} color="#059669" />

          <div style={{ ...sx.card, background: '#f0fdf4', display: 'flex', justifyContent: 'space-between', padding: '1rem 1.5rem' }}>
            <span style={{ fontWeight: 700 }}>(-) Costo de Ventas / Servicios</span>
            <span style={{ fontWeight: 700, color: '#dc2626' }}>RD$ {fmtM(estado.total_costos)}</span>
          </div>

          <div style={{ ...sx.card, display: 'flex', justifyContent: 'space-between', padding: '1rem 1.5rem', background: '#eff6ff' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>UTILIDAD BRUTA</span>
            <span style={{ fontWeight: 700, fontSize: '1rem', color: estado.utilidad_bruta >= 0 ? '#059669' : '#dc2626' }}>RD$ {fmtM(estado.utilidad_bruta)}</span>
          </div>

          <GrupoER titulo="GASTOS OPERATIVOS" items={estado.gastos} total={estado.total_gastos} color="#dc2626" />

          <div style={{ background: estado.utilidad_neta >= 0 ? '#1e3a8a' : '#7f1d1d', borderRadius: 10, padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>UTILIDAD NETA DEL PERÍODO</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>RD$ {fmtM(estado.utilidad_neta)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function GrupoER({ titulo, items, total, color }) {
  return (
    <div style={{ ...sx.card, marginBottom: '1rem' }}>
      <h3 style={{ ...sx.cardTitulo, color }}>{titulo}</h3>
      {items.map(i => (
        <div key={i.cuenta_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid #f1f3f5', fontSize: '0.875rem' }}>
          <span style={{ color: '#495057' }}>{i.codigo} – {i.nombre}</span>
          <span style={{ fontWeight: 600 }}>{fmtM(i.saldo)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.6rem', marginTop: '0.5rem', borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
        <span>Total {titulo.toLowerCase()}</span>
        <span style={{ color }}>RD$ {fmtM(total)}</span>
      </div>
    </div>
  );
}

// ─── Componentes auxiliares ────────────────────────
function Modal({ titulo, children, onClose, onGuardar, ancho = 700, labelGuardar = '💾 Guardar' }) {
  return (
    <div style={sx.overlay}>
      <div style={{ ...sx.modalBox, maxWidth: ancho }}>
        <div style={sx.modalHeader}>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>{titulo}</h2>
          <button onClick={onClose} style={sx.btnCerrar}>✕</button>
        </div>
        <div style={sx.modalBody}>{children}</div>
        <div style={sx.modalFooter}>
          <button onClick={onClose} style={sx.btnSecundario}>Cancelar</button>
          <button onClick={onGuardar} style={sx.btnPrimario}>{labelGuardar}</button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div style={{ ...sx.campo, marginBottom: '0.75rem' }}>
      <label style={sx.label}>{label}</label>
      {children}
    </div>
  );
}

function BadgeEstadoAsiento({ estado }) {
  const m = { borrador: ['#f3f4f6','#4b5563'], publicado: ['#d1fae5','#065f46'], anulado: ['#fee2e2','#991b1b'] };
  const [bg, color] = m[estado] || m.borrador;
  return <span style={{ ...sx.badge, background: bg, color }}>{estado}</span>;
}

function Spinner() {
  return <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>⏳ Cargando...</div>;
}

function aplanarCuentas(cuentas, nivel = 0) {
  const result = [];
  for (const c of cuentas) {
    result.push({ ...c, _nivel: nivel });
    if (c.hijos && c.hijos.length > 0) result.push(...aplanarCuentas(c.hijos, nivel + 1));
  }
  return result;
}

// ═══════════════════════════════════════════════════
//  Balanza de Comprobación
// ═══════════════════════════════════════════════════
function TabBalanza() {
  const [filas,    setFilas]    = useState([]);
  const [totales,  setTotales]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [desde,    setDesde]    = useState(primerDia);
  const [hasta,    setHasta]    = useState(hoyStr);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contabilidad/balanza?fecha_desde=${desde}&fecha_hasta=${hasta}`);
      setFilas(data.filas   || []);
      setTotales(data.totales || null);
    } catch (e) {
      Swal.fire('Error', 'No se pudo cargar la balanza', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const TIPO_COLOR = { activo: '#0ea5e9', pasivo: '#f97316', capital: '#8b5cf6', ingreso: '#22c55e', egreso: '#ef4444', costo: '#f59e0b' };

  return (
    <div style={{ padding: '1.5rem 0' }}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div>
          <label style={sx.label}>Desde</label>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={sx.input} />
        </div>
        <div>
          <label style={sx.label}>Hasta</label>
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={sx.input} />
        </div>
        <button onClick={cargar} style={sx.btnPrimario} disabled={loading}>
          {loading ? 'Cargando...' : 'Generar Balanza'}
        </button>
      </div>

      {filas.length === 0 && !loading && (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
          No hay movimientos contables en el período seleccionado.
        </div>
      )}

      {filas.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#1a1a2e', color: '#fff' }}>
                <th style={thS}>Código</th>
                <th style={thS}>Cuenta</th>
                <th style={{ ...thS, textAlign: 'right' }}>Débito Acum.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Crédito Acum.</th>
                <th style={{ ...thS, textAlign: 'right' }}>Saldo Deudor</th>
                <th style={{ ...thS, textAlign: 'right' }}>Saldo Acreedor</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={f.cuenta_id} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ ...tdS, fontFamily: 'monospace' }}>
                    <span style={{ background: TIPO_COLOR[f.tipo] || '#6b7280', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: '0.75rem' }}>
                      {f.codigo}
                    </span>
                  </td>
                  <td style={tdS}>{f.nombre}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#0369a1' }}>{fmtM(f.debito_total)}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#7c3aed' }}>{fmtM(f.credito_total)}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#15803d', fontWeight: f.saldo_deudor > 0 ? 600 : 400 }}>
                    {f.saldo_deudor > 0 ? fmtM(f.saldo_deudor) : '—'}
                  </td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#b91c1c', fontWeight: f.saldo_acreedor > 0 ? 600 : 400 }}>
                    {f.saldo_acreedor > 0 ? fmtM(f.saldo_acreedor) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            {totales && (
              <tfoot>
                <tr style={{ background: '#1a1a2e', color: '#fff', fontWeight: 700 }}>
                  <td colSpan={2} style={{ ...tdS, color: '#fff' }}>TOTALES</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#7dd3fc' }}>{fmtM(totales.debito_total)}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#c4b5fd' }}>{fmtM(totales.credito_total)}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#86efac' }}>{fmtM(totales.saldo_deudor)}</td>
                  <td style={{ ...tdS, textAlign: 'right', color: '#fca5a5' }}>{fmtM(totales.saldo_acreedor)}</td>
                </tr>
                <tr style={{ background: Math.abs(totales.saldo_deudor - totales.saldo_acreedor) < 0.01 ? '#dcfce7' : '#fee2e2' }}>
                  <td colSpan={6} style={{ ...tdS, textAlign: 'center', fontWeight: 700 }}>
                    {Math.abs(totales.saldo_deudor - totales.saldo_acreedor) < 0.01
                      ? '✓ Balanza cuadrada correctamente'
                      : `⚠ Diferencia: ${fmtM(Math.abs(totales.saldo_deudor - totales.saldo_acreedor))}`}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
const thS = { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdS = { padding: '0.6rem 1rem' };

const sx = {
  page:      { padding: '2rem', maxWidth: 1300, margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  titulo:    { fontSize: '2rem', fontWeight: 700, color: '#1a1a2e', margin: '0.5rem 0 0.25rem 0' },
  subtitulo: { color: '#6c757d', fontSize: '0.95rem', margin: 0 },
  btnVolver: { background: 'none', border: 'none', color: '#0066cc', cursor: 'pointer', fontSize: '0.9rem', padding: 0, fontWeight: 600 },
  tabsBar:   { display: 'flex', gap: '0.5rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem', overflowX: 'auto' },
  tabBtn:    { background: 'none', border: 'none', borderBottom: '3px solid transparent', padding: '0.7rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', color: '#6c757d', marginBottom: -2, whiteSpace: 'nowrap', transition: 'all 0.15s' },
  tabActivo: { color: '#0066cc', borderBottomColor: '#0066cc' },
  btnPrimario:  { background: '#0066cc', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.4rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnSecundario:{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' },
  card:      { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1rem' },
  cardTitulo:{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 0.75rem 0' },
  tablaWrap: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflowX: 'auto' },
  th:        { background: '#f8f9fa', padding: '0.7rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' },
  td:        { padding: '0.65rem 1rem', color: '#495057', borderBottom: '1px solid #f1f3f5', fontSize: '0.875rem' },
  badge:     { display: 'inline-block', padding: '0.25rem 0.65rem', borderRadius: 50, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modalBox:  { background: '#fff', borderRadius: 12, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  modalBody: { padding: '1.5rem' },
  modalFooter:{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' },
  btnCerrar: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6c757d' },
  fila2col:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  campo:     { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label:     { fontSize: '0.875rem', fontWeight: 600, color: '#495057' },
  input:     { padding: '0.6rem 0.75rem', border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  inputSm:   { padding: '0.5rem 0.7rem', border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.875rem', outline: 'none' },
  // Plan de cuentas
  btnIcono:  { background: 'none', border: '1px solid #dee2e6', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', padding: '0.25rem 0.5rem', marginRight: '0.35rem', lineHeight: 1.2, color: '#495057' },
  inputRO:   { background: '#f1f3f5', color: '#6c757d', cursor: 'not-allowed' },
  avisoInfo: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 8, padding: '0.7rem 0.9rem', fontSize: '0.85rem', marginBottom: '1rem' },
  avisoBloqueo: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: '0.7rem 0.9rem', fontSize: '0.85rem', marginBottom: '1rem', fontWeight: 600 }
};

// ═══════════════════════════════════════════════════
//  Cierres Contables
// ═══════════════════════════════════════════════════
function TabCierres() {
  const mesActual = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' }).substring(0, 7);
  const [cierres,   setCierres]   = useState([]);
  const [periodo,   setPeriodo]   = useState(mesActual);
  const [resumen,   setResumen]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [loadingR,  setLoadingR]  = useState(false);
  const [notas,     setNotas]     = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/cierres');
      setCierres(r.data.cierres || []);
    } catch (e) { Swal.fire('Error', 'No se pudieron cargar los cierres', 'error'); }
    finally { setLoading(false); }
  };

  const consultarResumen = async () => {
    if (!periodo) return;
    setLoadingR(true);
    setResumen(null);
    try {
      const r = await api.get(`/cierres/resumen/${periodo}`);
      setResumen(r.data);
    } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
    finally { setLoadingR(false); }
  };

  useEffect(() => { cargar(); }, []);
  useEffect(() => { consultarResumen(); }, [periodo]);

  const cerrar = async () => {
    if (!resumen) return;
    if (resumen.borradores_pendientes > 0)
      return Swal.fire('Borradores pendientes', `Hay ${resumen.borradores_pendientes} asiento(s) en borrador. Publícalos o elimínalos antes de cerrar.`, 'warning');

    const conf = await Swal.fire({
      title: `Cerrar período ${periodo}`,
      html: `
        <p>Ingresos: <b>${fmtM(resumen.totalIngresos)}</b></p>
        <p>Gastos: <b>${fmtM(resumen.totalGastos)}</b></p>
        <p>Resultado: <b style="color:${resumen.resultado >= 0 ? '#166534' : '#991b1b'}">${fmtM(resumen.resultado)}</b></p>
        <p style="color:#666;font-size:0.85rem;margin-top:8px">Esta acción bloqueará nuevos asientos en este período.</p>
      `,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Cerrar período', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e3a8a',
    });
    if (!conf.isConfirmed) return;

    try {
      await api.post('/cierres/cerrar', { periodo, notas });
      Swal.fire({ icon: 'success', title: `Período ${periodo} cerrado`, timer: 2000, showConfirmButton: false });
      cargar();
      consultarResumen();
    } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
  };

  const reabrir = async (p) => {
    const conf = await Swal.fire({ title: `¿Reabrir ${p}?`, text: 'Solo el superadmin puede hacerlo.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Reabrir', confirmButtonColor: '#dc3545' });
    if (!conf.isConfirmed) return;
    try {
      await api.post(`/cierres/reabrir/${p}`);
      Swal.fire({ icon: 'success', title: `Período ${p} reabierto`, timer: 1800, showConfirmButton: false });
      cargar();
      if (periodo === p) consultarResumen();
    } catch (e) { Swal.fire('Error', e.response?.data?.mensaje || 'Error', 'error'); }
  };

  const estadoCierre = resumen?.estado || 'abierto';
  const yaCerrado    = estadoCierre === 'cerrado';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>

      {/* Panel izquierdo — Cerrar período */}
      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1.25rem' }}>Cerrar período contable</h3>
        <div style={{ marginBottom: '1rem' }}>
          <label style={sx.label}>Período (YYYY-MM)</label>
          <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)} style={sx.input} />
        </div>

        {loadingR && <p style={{ color: '#6c757d', fontSize: '0.85rem' }}>Consultando...</p>}

        {resumen && !loadingR && (
          <div style={{ background: yaCerrado ? '#fef2f2' : '#f8f9fa', border: `1px solid ${yaCerrado ? '#fca5a5' : '#e9ecef'}`, borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.85rem', color: '#495057' }}>Estado</span>
              <span style={{ fontWeight: 700, color: yaCerrado ? '#991b1b' : '#166534' }}>{yaCerrado ? 'CERRADO' : 'ABIERTO'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.85rem' }}>Ingresos</span><span style={{ fontWeight: 600, color: '#166534' }}>{fmtM(resumen.totalIngresos)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.85rem' }}>Gastos</span><span style={{ fontWeight: 600, color: '#991b1b' }}>{fmtM(resumen.totalGastos)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #dee2e6', paddingTop: 6, marginTop: 6 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Resultado</span>
              <span style={{ fontWeight: 800, color: resumen.resultado >= 0 ? '#166534' : '#991b1b' }}>{fmtM(resumen.resultado)}</span>
            </div>
            {resumen.borradores_pendientes > 0 && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '0.5rem', marginTop: '0.75rem', fontSize: '0.82rem', color: '#856404' }}>
                {resumen.borradores_pendientes} asiento(s) en borrador — deben publicarse antes de cerrar
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={sx.label}>Notas (opcional)</label>
          <textarea value={notas} onChange={e => setNotas(e.target.value)} style={{ ...sx.input, height: 60, resize: 'vertical' }} placeholder="Observaciones del cierre..." />
        </div>

        {!yaCerrado ? (
          <button onClick={cerrar} style={{ background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: 8, padding: '0.65rem 1.25rem', cursor: 'pointer', fontWeight: 700, width: '100%' }}>
            Cerrar período {periodo}
          </button>
        ) : (
          <button onClick={() => reabrir(periodo)} style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, padding: '0.65rem 1.25rem', cursor: 'pointer', fontWeight: 700, width: '100%' }}>
            Reabrir período {periodo}
          </button>
        )}
      </div>

      {/* Panel derecho — Historial */}
      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1.25rem' }}>Historial de cierres</h3>
        {loading ? <Spinner /> : cierres.length === 0 ? (
          <p style={{ color: '#6c757d', textAlign: 'center', padding: '2rem 0' }}>No hay períodos cerrados</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead><tr>
                {['Período','Ingresos','Gastos','Resultado','Estado'].map(h =>
                  <th key={h} style={sx.th}>{h}</th>
                )}
              </tr></thead>
              <tbody>
                {cierres.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={{ ...sx.td, fontFamily: 'monospace', fontWeight: 700 }}>{c.periodo}</td>
                    <td style={{ ...sx.td, textAlign: 'right', color: '#166534' }}>{fmtM(c.total_ingresos)}</td>
                    <td style={{ ...sx.td, textAlign: 'right', color: '#991b1b' }}>{fmtM(c.total_gastos)}</td>
                    <td style={{ ...sx.td, textAlign: 'right', fontWeight: 700, color: parseFloat(c.resultado) >= 0 ? '#166534' : '#991b1b' }}>{fmtM(c.resultado)}</td>
                    <td style={sx.td}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 700, background: c.estado === 'cerrado' ? '#fee2e2' : '#fef9c3', color: c.estado === 'cerrado' ? '#991b1b' : '#854d0e' }}>
                        {c.estado === 'cerrado' ? 'Cerrado' : 'Reabierto'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
